import {
  type BuybackAccount,
  type BuybackAppliedLink,
  useTestBuybackAccountPhone,
  useRefreshBuybackAccount,
  useUpdateBuybackAccount,
  getListBuybackAccountsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PhoneStatusIcon } from "@/components/phone-status-icon";
import { cn } from "@/lib/utils";
import { Loader2, Send, RefreshCw, CalendarClock, ExternalLink } from "lucide-react";
import { Link } from "wouter";
import { toast } from "sonner";

// A buy-back account's phone health drives the same three-state ring the source
// accounts table uses, so the two categories read consistently.
type HealthState = "good" | "warning" | "bad";
function healthState(a: BuybackAccount): HealthState {
  if (a.phoneStatus === "good") return "good";
  if (a.phoneStatus === "bad") return "bad";
  return "warning";
}
const HEALTH_RING: Record<HealthState, string> = {
  good: "bg-green-500 ring-green-500/30",
  warning: "bg-amber-500 ring-amber-500/30",
  bad: "bg-red-500 ring-red-500/30",
};
const HEALTH_LABEL: Record<HealthState, string> = {
  good: "Good",
  warning: "Warning",
  bad: "Bad",
};

// Fewer TM purchases = fresher account = higher quality, so the count is tinted
// from green (unused) through amber to orange (heavily used).
function purchaseTier(count: number): string {
  if (count === 0) return "text-emerald-400";
  if (count < 3) return "text-amber-400";
  return "text-orange-400";
}

const EXCLUDE_LABEL: Record<string, string> = {
  bound_out: "Bound out",
  no_verification: "Not verified",
  stale_verification: "Verification stale",
  no_good_phone: "Phone unhealthy",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

// Turn an ISO timestamp into the `YYYY-MM-DD` a <input type="date"> expects.
function toDateInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function HealthRing({ account }: { account: BuybackAccount }) {
  const state = healthState(account);
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center">
            <span
              className={`h-2 w-2 rounded-full ring-2 ${HEALTH_RING[state]}`}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent>{HEALTH_LABEL[state]}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TestPhoneButton({ account }: { account: BuybackAccount }) {
  const qc = useQueryClient();
  const test = useTestBuybackAccountPhone({
    mutation: {
      onSuccess: (res) => {
        if (res.status === "sent")
          toast.success(`Test SMS sent to ${res.phoneNumber}`);
        else if (res.status === "skipped")
          toast.warning(res.error || "No phone on file");
        else toast.error(res.error || "Test SMS failed");
        qc.invalidateQueries({ queryKey: getListBuybackAccountsQueryKey() });
      },
      onError: () => toast.error("Twilio is not configured"),
    },
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-6 px-2 text-[10px]"
      disabled={test.isPending || !account.phone}
      onClick={() => test.mutate({ id: account.id })}
    >
      {test.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Send className="h-3 w-3" />
      )}
      <span className="ml-1">Test</span>
    </Button>
  );
}

function RefreshButton({ account }: { account: BuybackAccount }) {
  const qc = useQueryClient();
  const refresh = useRefreshBuybackAccount({
    mutation: {
      onSuccess: () => {
        toast.success("Buy-back account refreshed from AMP");
        qc.invalidateQueries({ queryKey: getListBuybackAccountsQueryKey() });
      },
      onError: () => toast.error("AMP (Zoho) is not configured"),
    },
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-6 px-2 text-[10px]"
      disabled={refresh.isPending}
      onClick={() => refresh.mutate({ id: account.id })}
    >
      {refresh.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      <span className="ml-1">Refresh</span>
    </Button>
  );
}

// Verified-date cell: shows the effective verified date (override wins over the
// raw AMP value) and lets the operator set/clear a manual override.
function VerifiedCell({ account }: { account: BuybackAccount }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(() =>
    toDateInput(account.emailLastVerifiedOverrideAt ?? account.lastVerified),
  );
  const update = useUpdateBuybackAccount({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListBuybackAccountsQueryKey() });
        setOpen(false);
      },
      onError: () => toast.error("Failed to update verified date"),
    },
  });
  const save = () => {
    if (!value) return;
    update.mutate({
      id: account.id,
      data: { emailLastVerifiedOverrideAt: new Date(value).toISOString() },
    });
  };
  const clear = () => {
    update.mutate({
      id: account.id,
      data: { emailLastVerifiedOverrideAt: null },
    });
  };
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o)
          setValue(
            toDateInput(
              account.emailLastVerifiedOverrideAt ?? account.lastVerified,
            ),
          );
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs hover:text-primary"
          title="Override verified date"
        >
          <CalendarClock className="h-3 w-3 text-muted-foreground" />
          <span className={cn(account.verifiedOverridden && "text-amber-400")}>
            {formatDate(account.lastVerified)}
          </span>
          {account.verifiedOverridden && (
            <span className="text-[8px] uppercase text-amber-400">ovr</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 space-y-2 p-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Override verified date
        </p>
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-8 text-xs"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-7 flex-1 text-xs"
            disabled={update.isPending || !value}
            onClick={save}
          >
            {update.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              "Save"
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={update.isPending || !account.emailLastVerifiedOverrideAt}
            onClick={clear}
          >
            Clear
          </Button>
        </div>
        {account.emailLastVerifiedAt && (
          <p className="text-[9px] text-muted-foreground">
            AMP: {formatDate(account.emailLastVerifiedAt)}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

// "Applied to" cell: a used pool account links out to each purchase row it's
// been set on (best-effort deep link into the workspace via the search focus).
function AppliedCell({ account }: { account: BuybackAccount }) {
  if (!account.used || account.appliedTo.length === 0) {
    return <span className="text-[10px] text-muted-foreground">Unused</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      {account.appliedTo.map((link: BuybackAppliedLink) => (
        <Link
          key={link.inventoryId}
          href={`/?focus=${encodeURIComponent(link.reachListingId)}`}
          className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
        >
          <ExternalLink className="h-2.5 w-2.5 shrink-0" />
          <span className="min-w-0 truncate">
            {link.eventName || `Listing ${link.reachListingId}`}
            {link.section ? ` · ${link.section}` : ""}
          </span>
        </Link>
      ))}
    </div>
  );
}

const HEAD =
  "text-left py-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium whitespace-nowrap";

export function BuybackAccountsTable({ data }: { data: BuybackAccount[] }) {
  return (
    <div className="w-full">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-panel z-10 border-b border-border shadow-sm">
          <tr>
            <th className={HEAD}></th>
            <th className={HEAD}>Email</th>
            <th className={HEAD}>Phone</th>
            <th className={HEAD}>Health</th>
            <th className={HEAD}>TM Buys</th>
            <th className={HEAD}>Bound Out</th>
            <th className={HEAD}>Quality</th>
            <th className={HEAD}>Email Verified</th>
            <th className={HEAD}>Applied To</th>
            <th className={HEAD}></th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((a) => (
              <tr
                key={a.id}
                className="border-b border-border/50 hover:bg-muted/50 transition-colors"
              >
                <td className="py-1.5 px-3">
                  <HealthRing account={a} />
                </td>
                <td className="py-1.5 px-3">
                  <div className="flex flex-col">
                    <span className="font-medium text-xs text-foreground">
                      {a.vendorAccountEmail}
                    </span>
                    {a.displayName && (
                      <span className="text-[10px] text-muted-foreground">
                        {a.displayName}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-1.5 px-3">
                  <span className="font-mono text-xs">{a.phone || "-"}</span>
                </td>
                <td className="py-1.5 px-3">
                  <PhoneStatusIcon
                    status={a.phoneStatus}
                    phoneType={a.phoneType}
                    phone={a.phone}
                    manual={a.phoneStatusManual}
                  />
                </td>
                <td className="py-1.5 px-3">
                  <span
                    className={cn("font-mono text-xs", purchaseTier(a.purchaseCount))}
                    title={`${a.purchaseCount} TM buy${a.purchaseCount === 1 ? "" : "s"}`}
                  >
                    {a.purchaseCount}
                  </span>
                </td>
                <td className="py-1.5 px-3">
                  {a.boundOut ? (
                    <Badge
                      variant="outline"
                      className="bg-red-500/10 text-red-400 border-red-500/30 font-mono text-[9px] uppercase"
                    >
                      Bound
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-1.5 px-3">
                  {a.eligible ? (
                    <Badge
                      variant="outline"
                      className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 font-mono text-[9px] uppercase"
                    >
                      Eligible
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-muted text-muted-foreground border-border font-mono text-[9px] uppercase"
                      title={
                        a.excludeReason
                          ? EXCLUDE_LABEL[a.excludeReason] ?? a.excludeReason
                          : undefined
                      }
                    >
                      {a.excludeReason
                        ? EXCLUDE_LABEL[a.excludeReason] ?? a.excludeReason
                        : "Ineligible"}
                    </Badge>
                  )}
                </td>
                <td className="py-1.5 px-3">
                  <VerifiedCell account={a} />
                </td>
                <td className="py-1.5 px-3 max-w-[220px]">
                  <AppliedCell account={a} />
                </td>
                <td className="py-1.5 px-3">
                  <div className="flex items-center gap-1.5">
                    <TestPhoneButton account={a} />
                    <RefreshButton account={a} />
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={10}
                className="text-center py-8 text-muted-foreground text-sm"
              >
                No buy-back accounts found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
